import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Table, TablePaginationConfig } from 'antd';
import cx from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import moment from 'moment';
import { nanoid } from '@reduxjs/toolkit';
import { isNil } from 'lodash';

import BasePageLabel from 'components/wrappers/BasePageLabel';
import { ColumnsType } from 'antd/es/table/interface';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getPlacementsDetails,
  handleBullhornConnectModalOpen,
} from 'redux/reducers/bullhornSlice';
import { useAppSelector } from 'hooks/useAppSelector';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { bullhornDisconnect } from 'redux/reducers/authenticationSlice';
import { bullhornEnableSelector } from 'redux/selectors/authenticationSelectors';
import {
  AssignmentBullhornViewModel,
  AssignmentBullhornViewModelPagedResponseModel,
} from 'api/backofficeApiClient';
import { AssignmentInfoTypes } from 'features/assignment/enums';
import { getAssignmentTypeLabel } from 'utils/assignmentUtils';
import { pageDefaultSizeTable } from 'constants/index';

import styles from './PlacementsDetailsPage.module.less';
import { formatNumberAsCurrency } from '../../../utils/numberUtils';

const columns: ColumnsType<AssignmentBullhornViewModel> = [
  {
    title: 'Candidate Name',
    key: 'candidateName',
    render: ({ candidateName }: AssignmentBullhornViewModel) => candidateName,
  },
  {
    title: 'Client/Company',
    key: 'clientCompany',
    render: ({ clientCompany }: AssignmentBullhornViewModel) => clientCompany,
  },
  {
    title: 'Type of Employment',
    key: 'typeOfEmployment',
    render: ({ assignmentType }: AssignmentBullhornViewModel) =>
      getAssignmentTypeLabel(assignmentType),
  },
  {
    title: 'Expected Start Date',
    key: 'startDate',
    render: ({ startDate }: AssignmentBullhornViewModel) =>
      startDate ? moment.utc(startDate).format('MM/DD/YYYY') : undefined,
  },
  {
    title: 'Pay Rate ($)',
    key: 'payRate',
    render: ({ payRate }: AssignmentBullhornViewModel) =>
      formatNumberAsCurrency({ num: payRate }),
  },
  {
    title: 'Bill Rate ($)',
    key: 'billRate',
    render: ({ billRate }: AssignmentBullhornViewModel) =>
      formatNumberAsCurrency({ num: billRate }),
  },
  {
    title: 'Assignment/Draft',
    key: 'assignment',
    render: ({ assignmentId, assignmentKey }: AssignmentBullhornViewModel) => {
      const isDeleted = isNil(assignmentKey) && isNil(assignmentId);

      if (isDeleted) return <span>N/A</span>;

      return assignmentKey ? (
        <Link
          to={`/assignment/${assignmentKey}?section=${AssignmentInfoTypes.Primary}`}
        >
          {assignmentKey}
        </Link>
      ) : (
        <Link
          to={`/assignment/${assignmentId}?section=${AssignmentInfoTypes.Primary}`}
        >
          Draft
        </Link>
      );
    },
  },
];

export default function PlacementsDetailsPage() {
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isPlacementsDetailsLoading, setIsPlacementsDetailsLoading] =
    useState(false);
  const [placementsDetails, setPlacementsDetails] =
    useState<AssignmentBullhornViewModelPagedResponseModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const bullhornEnable = useAppSelector(bullhornEnableSelector);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!bullhornEnable) {
      !isInitialDataLoaded && dispatch(handleBullhornConnectModalOpen(true));
      navigate('/');
    }
  }, [dispatch, bullhornEnable, navigate, isInitialDataLoaded]);

  const loadPlacementsDetails = useCallback(async () => {
    setIsPlacementsDetailsLoading(true);

    const result = await dispatch(
      getPlacementsDetails({
        pageNumber: currentTablePage - 1,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getPlacementsDetails.fulfilled.match(result)) {
      setPlacementsDetails(result.payload);
    } else {
      setPlacementsDetails({ details: [], totalCount: 0 });
    }

    setIsPlacementsDetailsLoading(false);
  }, [dispatch, currentTablePage]);

  const placementsTotalCountLabel = pluralize(
    'placement',
    placementsDetails?.totalCount,
    true,
  );

  const loadInitialData = useCallback(async () => {
    await loadPlacementsDetails();

    setIsInitialDataLoaded(true);
  }, [loadPlacementsDetails]);

  useEffect(() => {
    bullhornEnable && loadInitialData();
  }, [loadInitialData, bullhornEnable]);

  const handleBullhornDisconnect = () => {
    return Modal.confirm({
      title: 'Are you sure  you want to disconnect your Bullhorn account?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await onBullhornDisconnect();
      },
      onCancel() {},
    });
  };

  const onBullhornDisconnect = async () => {
    const result = await dispatch(bullhornDisconnect());

    if (bullhornDisconnect.fulfilled.match(result)) {
      BaseAlert.success('Your Bullhorn account was successfully disconnected.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentTablePage(pagination.current || 1);
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Bullhorn placements" />
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title=""
              enableSubTitle={isInitialDataLoaded}
              subTitle={placementsTotalCountLabel}
            >
              <Button onClick={handleBullhornDisconnect}>Disconnect</Button>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: placementsDetails?.totalCount,
                pageSize: pageDefaultSizeTable,
                showSizeChanger: false,
                current: currentTablePage,
              }}
              onChange={handleTableChange}
              showSorterTooltip={false}
              columns={columns}
              dataSource={placementsDetails?.details}
              rowKey={() => nanoid()}
              loading={isPlacementsDetailsLoading}
              scroll={{ x: '100%' }}
              locale={{
                emptyText: 'You currently do not have any placements.',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
